import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../components/loader/Loader";
import * as api from "./apiService";
import { toast } from "react-toastify";

const ArrowPathIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992V4.356"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.015 9.348A9 9 0 1 0 6.63 19.455"
    />
  </svg>
);

const ArrowRightIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 12h15m-6-6 6 6-6 6"
    />
  </svg>
);

const PlusIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4.5v15m7.5-7.5h-15"
    />
  </svg>
);

const ChevronLeftIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 19.5 8.25 12l7.5-7.5"
    />
  </svg>
);

const ChevronRightIcon = ({ className = "h-5 w-5" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className={className}
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m8.25 4.5 7.5 7.5-7.5 7.5"
    />
  </svg>
);

const CustomerActivity = () => {
  const { customerId } = useParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (!customerId) return;

    fetchActivity();
  }, [customerId, currentPage]);

  const fetchActivity = async (isRefresh = false) => {
    if (!customerId) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await api.getCustomerPriceListActivity(
        customerId,
        currentPage,
        pageSize,
      );

      setActivities(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      toast.error("Unable to load customer activity.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    await fetchActivity(true);
  };

  const getActivityConfig = (eventType) => {
    switch (eventType) {
      case "PRICE_LIST_ASSIGNED":
        return {
          label: "Price list assigned",
          Icon: PlusIcon,
        };

      case "PRICE_LIST_CHANGED":
        return {
          label: "Price list changed",
          Icon: ArrowPathIcon,
        };

      default:
        return {
          label: "Price list updated",
          Icon: ArrowPathIcon,
        };
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";

    return name
      .trim()
      .split(/\s+/)
      .map((part) => part[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const formatDate = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleDateString("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleTimeString("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  const firstResult = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const lastResult = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="mx-auto mt-2 max-w-7xl px-4 sm:px-6 lg:px-8 lg:pr-52">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 py-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Activity</h2>

          <p className="mt-1 text-sm text-gray-500">
            View price list changes and assignments made to this customer.
          </p>
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="
            inline-flex items-center gap-2
            rounded-lg border border-gray-300
            bg-white px-4 py-2
            text-sm font-semibold text-gray-700
            shadow-sm transition
            hover:bg-gray-50 hover:text-gray-900
            disabled:cursor-not-allowed disabled:opacity-50
          "
        >
          <ArrowPathIcon
            className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
          />

          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Content */}
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-xl border border-gray-200">
            <Loader />
          </div>
        ) : activities.length === 0 ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50/30 px-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <ArrowPathIcon className="h-6 w-6 text-gray-400" />
            </div>

            <h3 className="mt-4 text-sm font-semibold text-gray-900">
              No activity yet
            </h3>

            <p className="mt-1 max-w-sm text-sm text-gray-500">
              Price list assignments and changes made to this customer will
              appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      Activity
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      Details
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      Changed By
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      Date &amp; Time
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100 bg-white">
                  {activities.map((activity) => {
                    const { label, Icon } = getActivityConfig(
                      activity.event_type,
                    );

                    return (
                      <tr
                        key={activity.id}
                        className="transition hover:bg-gray-50/70"
                      >
                        {/* Activity */}
                        <td className="whitespace-nowrap px-6 py-5">
                          <div className="flex items-center gap-4">
                            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-sky-50 text-sky-600">
                              <Icon className="h-5 w-5" />
                            </div>

                            <span className="text-sm font-semibold text-gray-900">
                              {label}
                            </span>
                          </div>
                        </td>

                        {/* Details */}
                        <td className="px-6 py-5">
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-medium text-gray-700">
                              {activity.old_price_list_name || "None"}
                            </span>

                            <ArrowRightIcon className="h-4 w-4 flex-none text-gray-300" />

                            <span className="font-semibold text-red-600">
                              {activity.new_price_list_name || "None"}
                            </span>
                          </div>

                          {activity.note ? (
                            <p className="mt-1 max-w-xl text-xs text-gray-500">
                              {activity.note}
                            </p>
                          ) : null}
                        </td>

                        {/* Changed By */}
                        <td className="whitespace-nowrap px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                              {getInitials(activity.actor_name)}
                            </div>

                            <span className="text-sm font-medium text-gray-700">
                              {activity.actor_name}
                            </span>
                          </div>
                        </td>

                        {/* Date */}
                        <td className="whitespace-nowrap px-6 py-5">
                          <div className="text-sm font-medium text-gray-700">
                            {formatDate(activity.created_at)}
                          </div>

                          <div className="mt-1 text-xs text-gray-500">
                            {formatTime(activity.created_at)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-5 py-4">
              <p className="text-sm text-gray-500">
                Showing{" "}
                <span className="font-medium text-gray-700">{firstResult}</span>{" "}
                to{" "}
                <span className="font-medium text-gray-700">{lastResult}</span>{" "}
                of{" "}
                <span className="font-medium text-gray-700">{totalCount}</span>{" "}
                {totalCount === 1 ? "entry" : "entries"}
              </p>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                  className="
                    inline-flex h-9 w-9 items-center justify-center
                    rounded-md border border-gray-300
                    bg-white text-gray-500
                    shadow-sm transition
                    hover:bg-gray-50 hover:text-gray-700
                    disabled:cursor-not-allowed disabled:opacity-40
                  "
                >
                  <span className="sr-only">Previous page</span>

                  <ChevronLeftIcon className="h-4 w-4" />
                </button>

                <div className="inline-flex h-9 min-w-[36px] items-center justify-center rounded-md bg-red-600 px-3 text-sm font-semibold text-white shadow-sm">
                  {currentPage}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages || loading}
                  onClick={() => setCurrentPage((page) => page + 1)}
                  className="
                    inline-flex h-9 w-9 items-center justify-center
                    rounded-md border border-gray-300
                    bg-white text-gray-500
                    shadow-sm transition
                    hover:bg-gray-50 hover:text-gray-700
                    disabled:cursor-not-allowed disabled:opacity-40
                  "
                >
                  <span className="sr-only">Next page</span>

                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerActivity;
